import { router } from '@inertiajs/react';
import { EyeOff, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toastFlash } from '@/lib/flash-toast';
import type { StageRanking } from '@/types';

// Publishing puts every team's results on its hackers' dashboards (and unpublishing takes
// them down). The confirmation warns about any stage whose ranking is still provisional.
export function PublishResults({
  eventSlug,
  publishedAt,
  rankings,
}: {
  eventSlug: string;
  publishedAt: string | null;
  rankings: StageRanking[];
}) {
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);
  const provisional = rankings.filter(
    (ranking) => ranking.judgeCount > 0 && !ranking.complete,
  );
  const url = `/admin/${eventSlug}/judging/publication`;

  const submit = () => {
    setProcessing(true);
    const options = {
      preserveScroll: true,
      onSuccess: (page: Parameters<typeof toastFlash>[0]) =>
        toastFlash(page, publishedAt ? 'Results unpublished' : 'Results published'),
      onError: () =>
        toast({ type: 'error', description: 'Something went wrong' }),
      onFinish: () => {
        setProcessing(false);
        setConfirming(false);
      },
    };
    if (publishedAt) router.delete(url, options);
    else router.post(url, {}, options);
  };

  return (
    <div className="flex items-center gap-2">
      {publishedAt && (
        <Badge className="gap-1">
          <Send className="h-3 w-3" />
          Published{' '}
          {new Date(publishedAt).toLocaleString('en', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Badge>
      )}
      <Button
        variant={publishedAt ? 'outline' : 'default'}
        onClick={() => setConfirming(true)}
      >
        {publishedAt ? (
          <EyeOff className="mr-2 h-4 w-4" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        {publishedAt ? 'Unpublish results' : 'Publish results'}
      </Button>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {publishedAt ? 'Unpublish results?' : 'Publish results?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2">
                <p>
                  {publishedAt
                    ? 'The results disappear from the hackers’ dashboards.'
                    : 'Every hacker will see their team’s 0–100 scores, per criterion and final, with their place in their stage, on their dashboard. The numbers stay live: later grade changes show up too.'}
                </p>
                {!publishedAt && provisional.length > 0 && (
                  <p className="font-medium text-destructive">
                    Still provisional:{' '}
                    {provisional.map((ranking) => ranking.stageName).join(', ')}{' '}
                    — not every judge has finalised or some ballots are
                    missing.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={processing}>
              {publishedAt ? 'Unpublish' : 'Publish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
